import React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 dark:from-gray-900 dark:to-gray-800 p-4" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-slate-600 hover:text-slate-900"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="shadow-2xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl sm:text-3xl font-bold">Privacy Policy</CardTitle>
            <p className="text-sm text-slate-500 mt-2">Last updated: May 2026</p>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none px-6 sm:px-10 pb-8 text-sm sm:text-base text-slate-700 leading-relaxed space-y-6">

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">1. Introduction</h3>
              <p>
                Welcome to TaskPriority Prime ("we," "our," or "us"). We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our web application at taskpriority.net (the "Service").
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">2. Information We Collect</h3>
              <p className="mb-2">We collect information that you provide directly to us:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Account Information:</strong> When you create an account, we collect your email address and password (stored in encrypted/hashed form).</li>
                <li><strong>Task Data:</strong> Tasks you create, including titles, descriptions, categories, due dates, and completion status.</li>
                <li><strong>Journal Entries:</strong> Any notes or journal entries you create within the Service.</li>
                <li><strong>Usage Data:</strong> We may collect information about how you access and use the Service, including your browser type, device type, and general usage patterns.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">3. How We Use Your Information</h3>
              <p className="mb-2">We use the information we collect to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Provide, maintain, and improve the Service</li>
                <li>Authenticate your identity and manage your account</li>
                <li>Store and display your tasks and journal entries</li>
                <li>Send you service-related communications (such as password resets)</li>
                <li>Monitor and analyze usage trends to improve user experience</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">4. Data Storage and Retention</h3>
              <p>
                Your data is stored on secure servers. Task and journal data is retained for approximately 30 days. We use industry-standard security measures to protect your information, including encrypted passwords and secure database connections. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">5. Third-Party Services</h3>
              <p className="mb-2">We may use the following third-party services:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Google OAuth:</strong> If you choose to sign in with Google, we receive your name and email address from Google. We do not receive or store your Google password.</li>
                <li><strong>Google AdSense:</strong> We use Google AdSense to display advertisements. Google may use cookies and similar technologies to serve ads based on your prior visits to our website or other websites. You can opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">6. Cookies</h3>
              <p>
                We use cookies and similar technologies to maintain your session, remember your preferences (such as theme and layout settings), and support advertising through Google AdSense. You can control cookie settings through your browser preferences.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">7. Data Sharing</h3>
              <p>
                We do not sell, trade, or otherwise transfer your personal information to third parties, except as described in this policy (e.g., Google OAuth, Google AdSense). We may disclose your information if required by law or to protect our rights and safety.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">8. Your Rights</h3>
              <p className="mb-2">You have the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your account and associated data</li>
                <li>Opt out of personalized advertising</li>
              </ul>
              <p className="mt-2">To exercise any of these rights, please contact us at the email address below.</p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">9. Children's Privacy</h3>
              <p>
                The Service is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">10. Changes to This Policy</h3>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by updating the "Last updated" date at the top of this page. Your continued use of the Service after any changes constitutes your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">11. Contact Us</h3>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:{' '}
                <a href="mailto:rpfarrah@gmail.com" className="text-blue-600 hover:underline">rpfarrah@gmail.com</a>
              </p>
            </section>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;
